
/**
 * Encryption Service
 * Implements AES-GCM 256-bit client-side encryption.
 * Supports deriving keys from passwords or identity tokens.
 */

const ALGORITHM = 'AES-GCM';
const SALT = 'NomineeDesk_Global_Salt_V2_2024'; 

export const encryptionService = {
  /**
   * Derives a cryptographic key from a secret (password or sub).
   */
  async deriveKey(secret: string): Promise<CryptoKey> {
    const enc = new TextEncoder();
    const keyMaterial = await window.crypto.subtle.importKey(
      'raw',
      enc.encode(secret + SALT),
      { name: 'PBKDF2' },
      false,
      ['deriveBits', 'deriveKey']
    );

    return window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: enc.encode(SALT),
        iterations: 100000,
        hash: 'SHA-256',
      },
      keyMaterial,
      { name: ALGORITHM, length: 256 },
      false,
      ['encrypt', 'decrypt']
    );
  },

  /**
   * Generates a separate hash for authentication (so we don't use the encryption key for login).
   */
  async hashForAuth(password: string, email: string): Promise<string> {
    const enc = new TextEncoder();
    const data = enc.encode(password + email + SALT);
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
  },

  /**
   * Encrypts a JS object into a Base64 string.
   */
  async encrypt(data: any, secret: string): Promise<string> {
    const key = await this.deriveKey(secret);
    const enc = new TextEncoder();
    const encodedData = enc.encode(JSON.stringify(data));
    const iv = window.crypto.getRandomValues(new Uint8Array(12));
    
    const ciphertext = await window.crypto.subtle.encrypt(
      { name: ALGORITHM, iv },
      key,
      encodedData
    );

    const combined = new Uint8Array(iv.length + ciphertext.byteLength);
    combined.set(iv);
    combined.set(new Uint8Array(ciphertext), iv.length);

    return btoa(String.fromCharCode(...combined));
  },

  /**
   * Decrypts a Base64 string back into a JS object.
   */
  async decrypt(base64Data: string, secret: string): Promise<any> {
    try {
      const key = await this.deriveKey(secret);
      const combined = new Uint8Array(
        atob(base64Data).split('').map(c => c.charCodeAt(0))
      );
      
      const iv = combined.slice(0, 12);
      const ciphertext = combined.slice(12);

      const decrypted = await window.crypto.subtle.decrypt(
        { name: ALGORITHM, iv },
        key,
        ciphertext
      );

      const dec = new TextDecoder();
      return JSON.parse(dec.decode(decrypted));
    } catch (e) {
      console.error("Decryption failed.", e);
      return null;
    }
  }
};
